import {useState, useEffect, useRef} from 'react';
import {useNavigate} from "react-router";
import {useConfirm} from "material-ui-confirm";
import {useTranslation} from "react-i18next";
import useAlert from "../useAlert";
import * as queryString from 'query-string';
import {hasWaitSingleton, resetWaitSingleton, waitSingleton} from "../../utils/wait";

export function useGrid(repository, {onEdit, defaultFilter = {}, defaultSort = ''}) {

	const envFilter = queryString.parse(window.location.search);

    const { t }  = useTranslation();
    const [list, setList] = useState([]);
	const [page, setPage] = useState(0);
    const [filter, setFilter] = useState(Object.assign(defaultFilter, envFilter))
    const [sort, setSort] = useState(defaultSort);
	const [isSearching, setIsSearching] = useState(false);
	const itemsOnPage = 50;
    const {showSuccess, showError} = useAlert();
    const [triggerUpdate, setTriggerUpdate] = useState(false);

	const navigate = useNavigate();
	const confirm = useConfirm()
    const isFirstRun = useRef(true);

	const items = () => {
		return (list.Items || []).map(x => Object.assign(x, { id: x.Id }));
	};

	const totalCount = () => {
		return list.TotalCount || 0;
    }

    const getData = async () => {
        try {
            setIsSearching(true);
            const response = await repository.getAll(Object.assign({}, filter, { sort: sort, page: page + 1, itemsOnPage: itemsOnPage }));
            setList(response.data);
        } catch (err) {
            console.log(err);
            if (typeof(err) == 'string') {
                showError(err);
            }
        } finally {
            setIsSearching(false);
        }
    };

	const handlePageChange = (event) => {
        setPage(event);
    }

	useEffect(() => {
        if (isFirstRun.current) {
            return;
        }
		getData();
	}, [page]);

	const handleDeleteItemClick = async (event) => {

        confirm({ description: t('Do you really want to delete this item ?') })
         .then(async () => {
             try {
                 const response = await repository.delete(event.Id);
                 getData();
                 showSuccess(t('Item has been deleted'));
            } catch (err) {
                 showError(t('Error deleting item. Please try again.'));
            }
         }).catch(() => {})
    }

    const handleEditItemClick = (event) => {
	    onEdit(event);
    }

    const onFilterChangedHandler = (event) => {
        setFilter(Object.assign({}, filter, event.filter));
    }

    const onSortChangedHandler = (event) => {
        setSort(event.sort);
    }

    const handleUpdateGrid = () => {
	    setFilter(Object.assign({}, filter));
	    setTriggerUpdate(true);
    }

    const setFilterValue = (key, value) => {
	    if (key == null && typeof(value) == 'object') {
	        setFilter(Object.assign({}, filter, value))
        } else {
	        setFilter(Object.assign({}, filter, { [key]: value }))
        }
    }

    useEffect(() => {

        if (isFirstRun.current) {
            return;
        }

        if (!triggerUpdate) {
            waitSingleton(4000).then(() => {
                getData();
            })
        } else {
            setTriggerUpdate(false);
            getData();
            if (hasWaitSingleton()) {
                resetWaitSingleton();
            }
        }
    }, [filter]);

    useEffect(() => {

        if (isFirstRun.current) {
            return;
        }
        getData();
    }, [sort]);

	useEffect(() => {
	    navigate({search: '?' + queryString.stringify(filter)});
    }, [filter]);

	useEffect(() => {

        if (isFirstRun.current) {
            isFirstRun.current = false;
            getData();
        }
	});

    return {
	      items: items,
          totalCount: totalCount,
          page: page,
          itemsOnPage:
          itemsOnPage,
          isSearching: isSearching,
          filter: filter,
          sort: sort,
          setFilterValue: setFilterValue,
          onFilterChangedHandler: onFilterChangedHandler,
          onSortChangedHandler: onSortChangedHandler,
          onDeleteHandler: handleDeleteItemClick,
          onEditHandler: handleEditItemClick,
          onPage: handlePageChange,
          updateGrid: handleUpdateGrid};
}